import { type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDateTime, formatEuro } from '@rental/i18n'
import { Button, Checkbox, cn, Field, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api } from '../api'
import { DataTable } from '../components/DataTable'
import { LookupProblem } from '../components/LookupProblem'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { Fetching } from '../components/Results'
import { ReasonList, SubPanel } from '../components/SubPanel'
import {
  FIELD_ROW,
  FieldPair,
  NamedFor,
  PAIRED_FIELD,
  ProblemNotice,
  SHORT_FIELD,
  StaffInput,
} from '../components/ui'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import { centsForInput } from '../money'
import type { StaffProblem } from '../problems'
import { romeNow } from '../time'
import { FISCAL_QUERY_KEY } from './Documents'
import {
  figuresChanged,
  followNow,
  newTillDraft,
  type TillDifference,
  type TillDraft,
  tillBody,
  tillDifferenceOf,
} from './till'

type StaffTillClosure = ApiSchemas['StaffTillClosure']

/** Where the Zs are read: Cassa, for every point at once. */
export const TILLS_QUERY_KEY = [...FISCAL_QUERY_KEY, 'tills'] as const

/**
 * The Z as printed: its number, when, and its total. Once the server has
 * said it differs from the till's documents, the figures and a reason. Short
 * fields, whose words never grow: they pair from 128 px (`SHORT_FIELD`).
 */
export function TillFields({
  said,
  draft,
  onChange,
  difference,
  missing,
  focusReason = false,
}: {
  /**
   * Whose Z it is, said after each field's label (UI-2g): the drawer's point at
   * the close; a Z typed alone or a correction adds its form's name, since the
   * drawer's own Z can be on the screen beside it.
   */
  said: string
  draft: TillDraft
  onChange: (draft: TillDraft) => void
  difference: TillDifference | null
  missing: 'incomplete' | 'reason' | null
  /**
   * The server said the Z differs: its reason takes the focus (UI-2g). Where the button
   * waits for the reason, it is disabled the moment the difference comes, and the focus
   * it held would be left on nothing.
   */
  focusReason?: boolean
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.fiscal.till
  const reasonField = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (focusReason && difference) reasonField.current?.focus()
  }, [focusReason, difference])
  return (
    <div className="flex flex-col gap-3">
      <div className={FIELD_ROW}>
        <Field
          className={SHORT_FIELD}
          label={
            <>
              {copy.number}
              <NamedFor name={said} />
            </>
          }
        >
          {(control) => (
            <StaffInput
              {...control}
              inputMode="numeric"
              autoComplete="off"
              value={draft.number}
              onChange={(event) => onChange({ ...draft, number: event.target.value })}
            />
          )}
        </Field>
        <Field
          className={SHORT_FIELD}
          label={
            <>
              {copy.total}
              <NamedFor name={said} />
            </>
          }
          hint={copy.totalHint}
        >
          {(control) => (
            <StaffInput
              {...control}
              inputMode="decimal"
              autoComplete="off"
              value={draft.total}
              onChange={(event) => onChange({ ...draft, total: event.target.value })}
            />
          )}
        </Field>
        <FieldPair>
          <Field
            className={PAIRED_FIELD}
            label={
              <>
                {copy.date}
                <NamedFor name={said} />
              </>
            }
          >
            {(control) => (
              <StaffInput
                {...control}
                type="date"
                value={draft.date}
                onChange={(event) => onChange({ ...draft, date: event.target.value })}
              />
            )}
          </Field>
          <Field
            className={PAIRED_FIELD}
            label={
              <>
                {copy.time}
                <NamedFor name={said} />
              </>
            }
          >
            {(control) => (
              <StaffInput
                {...control}
                type="time"
                value={draft.time}
                onChange={(event) => onChange({ ...draft, time: event.target.value })}
              />
            )}
          </Field>
        </FieldPair>
      </div>
      {missing === 'incomplete' ? (
        <p className="text-sm font-medium text-danger" role="alert">
          {copy.incomplete}
        </p>
      ) : null}
      {difference ? (
        <>
          <Notice tone="warning" live="polite">
            <p>
              {format(copy.difference, {
                documents: formatEuro(difference.documentsCents, locale),
                difference: formatEuro(difference.differenceCents, locale),
              })}
            </p>
          </Notice>
          <Field
            label={
              <>
                {copy.reason}
                <NamedFor name={said} />
              </>
            }
            hint={copy.reasonHint}
            required
            error={missing === 'reason' ? m.refund.reasonShort : null}
          >
            {(control) => (
              <StaffInput
                {...control}
                ref={reasonField}
                maxLength={500}
                autoComplete="off"
                value={draft.reason}
                onChange={(event) => onChange({ ...draft, reason: event.target.value })}
              />
            )}
          </Field>
        </>
      ) : null}
    </div>
  )
}

/**
 * A Z's fields with the server's last answer: stale once its figures change.
 * A new Z's suggested time follows the clock until the counter types one.
 */
export function useTillDraft(initial: () => TillDraft, options: { suggestNow?: boolean } = {}) {
  const [draft, setDraft] = useState(initial)
  const [difference, setDifference] = useState<TillDifference | null>(null)
  const [missing, setMissing] = useState<'incomplete' | 'reason' | null>(null)
  const [timeTyped, setTimeTyped] = useState(false)
  function change(typed: TillDraft) {
    const timeChanged = typed.date !== draft.date || typed.time !== draft.time
    if (timeChanged) setTimeTyped(true)
    const next = options.suggestNow && !timeTyped ? followNow(draft, typed, romeNow()) : typed
    if (figuresChanged(draft, next)) setDifference(null)
    setMissing(null)
    setDraft(next)
  }
  /** Recorded: the fields start again, so the same Z is never sent twice. */
  function clear() {
    setDraft(initial())
    setDifference(null)
    setMissing(null)
    setTimeTyped(false)
  }
  return { draft, change, difference, setDifference, missing, setMissing, clear }
}

/** The server said the Z differs: its figures, or null for any other problem. */
export function differenceFrom(problem: StaffProblem): TillDifference | null {
  return problem.kind === 'api' && problem.code === 'till_difference'
    ? tillDifferenceOf(problem.details)
    : null
}

/** What a Z's button waits for, in the words the form uses. */
function whyOf(
  missing: 'incomplete' | 'reason',
  copy: { incomplete: string; whyReason: string },
): string {
  return missing === 'incomplete' ? copy.incomplete : copy.whyReason
}

/**
 * A point's Z typed on its own: a closure made without closing the drawer, or
 * a missed one. A form of its own on a well, named by its heading; Annulla
 * leaves it unsent.
 */
export function TillForm({
  locationId,
  point,
  onDone,
  onCancel,
}: {
  locationId: string
  point: string
  onDone: () => void
  onCancel: () => void
}) {
  const { messages: m } = useLocale()
  const copy = m.fiscal.till
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const titleId = useId()
  // The drawer's Z can be on the screen too, in its close: these fields say their form.
  const said = `${copy.aloneTitle}, ${point}`
  const till = useTillDraft(() => newTillDraft(), { suggestNow: true })
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const reasonsId = useId()
  // What the button waits for, in words (UI-2g): it was pressed to find out.
  const check = tillBody(till.draft, { optional: false, difference: till.difference })
  const reasons = check.ok || busy ? [] : [whyOf(check.missing, copy)]

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    const outcome = tillBody(till.draft, { optional: false, difference: till.difference })
    if (!outcome.ok) {
      till.setMissing(outcome.missing)
      return
    }
    if (!outcome.till) return
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/fiscal/till-closures', {
        body: { locationId, closure: outcome.till },
      }),
    )
    setBusy(false)
    if (result.ok) {
      await queryClient.invalidateQueries({ queryKey: FISCAL_QUERY_KEY })
      onDone()
      return
    }
    const difference = differenceFrom(result.problem)
    if (difference) {
      till.setDifference(difference)
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  return (
    <form onSubmit={submit} onKeyDown={noImplicitSubmit} aria-labelledby={titleId} noValidate>
      <SubPanel
        title={
          <span id={titleId}>
            {copy.aloneTitle}
            <NamedFor name={point} />
          </span>
        }
      >
        <p className="text-sm text-text-muted">{copy.aloneHint}</p>
        <TillFields
          draft={till.draft}
          onChange={till.change}
          difference={till.difference}
          missing={till.missing}
          focusReason
          said={said}
        />
        <ProblemNotice problem={problem} />
        <div className="flex flex-wrap gap-3">
          <Button
            type="submit"
            variant="staff"
            busy={busy}
            disabled={reasons.length > 0}
            aria-describedby={reasons.length > 0 ? reasonsId : undefined}
          >
            {copy.submit}
            <NamedFor name={point} />
          </Button>
          <Button variant="outline" className="rounded-sm" disabled={busy} onClick={onCancel}>
            {m.fiscal.cancel}
            <NamedFor name={said} />
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={reasons} />
      </SubPanel>
    </form>
  )
}

/** A Z typed wrong: superseded with a reason and kept; the right one may follow. */
function CorrectTill({
  wrong,
  point,
  onDone,
  onCancel,
}: {
  wrong: StaffTillClosure
  point: string
  onDone: () => void
  onCancel: () => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.fiscal.till
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [withRight, setWithRight] = useState(true)
  const till = useTillDraft(() => {
    const printed = romeNow(new Date(wrong.closedAt))
    return {
      number: String(wrong.zNumber),
      date: printed.date,
      time: printed.time,
      total: `${wrong.totalCents < 0 ? '-' : ''}${centsForInput(Math.abs(wrong.totalCents), locale)}`,
      reason: '',
    }
  })
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const reasonOk = reason.trim().length >= 3
  const reasonsId = useId()
  // The right Z, when it is typed with the correction, says what it still needs too (UI-2g).
  const right = withRight
    ? tillBody(till.draft, { optional: false, difference: till.difference })
    : null
  const reasons = busy
    ? []
    : [
        ...(reasonOk ? [] : [m.fiscal.why.reason]),
        ...(right && !right.ok ? [whyOf(right.missing, copy)] : []),
      ]

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (reasons.length > 0 || busy) return
    let closure: ReturnType<typeof tillBody> | null = null
    if (withRight) {
      closure = tillBody(till.draft, { optional: false, difference: till.difference })
      if (!closure.ok) {
        till.setMissing(closure.missing)
        return
      }
    }
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/fiscal/till-closures/{id}/correction', {
        params: { path: { id: wrong.id } },
        body: {
          reason: reason.trim(),
          ...(closure?.ok && closure.till ? { closure: closure.till } : {}),
        },
      }),
    )
    setBusy(false)
    if (result.ok) {
      await queryClient.invalidateQueries({ queryKey: FISCAL_QUERY_KEY })
      onDone()
      return
    }
    const difference = differenceFrom(result.problem)
    if (difference) {
      till.setDifference(difference)
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  // Named, not a heading: it opens inside the list's table, under the Z it corrects.
  const title = format(copy.correctTitle, { number: wrong.zNumber })
  const said = `${title}, ${point}`
  return (
    <form
      onSubmit={submit}
      onKeyDown={noImplicitSubmit}
      aria-label={said}
      className="flex flex-col gap-3 rounded-sm bg-surface-well p-4"
      noValidate
    >
      <p className="text-lg font-bold text-brand-strong">{title}</p>
      <Field
        label={
          <>
            {m.fiscal.correctReason}
            <NamedFor name={point} />
          </>
        }
        required
        error={reason.trim().length > 0 && !reasonOk ? m.refund.reasonShort : null}
      >
        {(control) => (
          <StaffInput
            {...control}
            maxLength={500}
            autoComplete="off"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>
      <Checkbox
        label={
          <>
            {copy.correctWithRight}
            <NamedFor name={point} />
          </>
        }
        checked={withRight}
        onChange={(event) => setWithRight(event.target.checked)}
      />
      {withRight ? (
        <TillFields
          draft={till.draft}
          onChange={till.change}
          difference={till.difference}
          missing={till.missing}
          focusReason
          said={said}
        />
      ) : null}
      <ProblemNotice problem={problem} />
      <div className="flex flex-wrap gap-3">
        <Button
          type="submit"
          variant="staff"
          busy={busy}
          disabled={reasons.length > 0}
          aria-describedby={reasons.length > 0 ? reasonsId : undefined}
        >
          {copy.correctSubmit}
          <NamedFor name={point} />
        </Button>
        <Button variant="outline" className="rounded-sm" onClick={onCancel} disabled={busy}>
          {m.fiscal.cancel}
          <NamedFor name={said} />
        </Button>
      </div>
      <ReasonList id={reasonsId} reasons={reasons} />
    </form>
  )
}

/**
 * The Zs of every point as read for Cassa: undefined until the list is in.
 * A problem reading them stays beside the list, never in its place.
 */
export interface TillsRead {
  closures: readonly StaffTillClosure[] | undefined
  problem: StaffProblem | null
  retrying: boolean
  retry: () => Promise<{ isSuccess: boolean }>
}

/**
 * A point's latest Zs, newest first: each as it was typed, and its difference
 * now, documents typed since included; a corrected one kept, with why. While
 * the list comes it says so, and a list that did not come says that, with
 * Riprova: never "no Z" for a list it has not read (UI-2e). Each Correggi is
 * named by its Z; the correction opens under its row and takes the focus, and
 * Annulla gives it back to that Correggi.
 */
export function TillClosures({ point, read }: { point: string; read: TillsRead }) {
  const { locale, messages: m } = useLocale()
  const copy = m.fiscal.till
  const { access } = useAccess()
  const mayCorrect = can(access, 'payments.record')
  const [open, setOpen] = useState<string | null>(null)
  const [corrected, setCorrected] = useState<number | null>(null)
  const list = useRef<HTMLDivElement>(null)
  // Where Riprova leaves the focus once the Zs come: their heading, read on from there (UI-2g).
  const heading = useRef<HTMLHeadingElement>(null)
  const moved = useRef<{ to: 'form' | 'button'; id: string } | null>(null)
  const euro = (cents: number) => formatEuro(cents, locale)

  useEffect(() => {
    const target = moved.current
    moved.current = null
    if (!target || !list.current) return
    if (target.to === 'form' && open === target.id) {
      list.current.querySelector<HTMLElement>('form input')?.focus()
    }
    if (target.to === 'button' && open === null) {
      list.current.querySelector<HTMLElement>(`button[data-z="${target.id}"]`)?.focus()
    }
  }, [open])

  const { closures, problem } = read
  return (
    <div ref={list} className="flex flex-col gap-3">
      <h3 ref={heading} tabIndex={-1} className="text-lg focus:outline-none">
        {copy.list}
        <NamedFor name={point} />
      </h3>
      {corrected === null ? null : (
        <OutcomeNotice key={corrected}>
          <p>{copy.corrected}</p>
        </OutcomeNotice>
      )}
      <LookupProblem
        problem={problem}
        what={copy.listFailed}
        busy={read.retrying}
        retry={read.retry}
        target={heading}
        retryFor={point}
      />
      {closures === undefined ? (
        problem ? null : (
          <Fetching />
        )
      ) : closures.length === 0 ? (
        <p className="text-text-muted">{copy.none}</p>
      ) : (
        <>
          <DataTable
            label={format(copy.listOf, { point })}
            rows={closures}
            rowKey={(closure) => closure.id}
            rowMarks={(closure) => ({
              testId: `z-${closure.zNumber}`,
              superseded: closure.supersededAt !== null,
            })}
            columns={[
              {
                header: copy.columns.number,
                cell: (closure) => closure.zNumber,
                numeric: true,
                rowHeader: true,
              },
              {
                header: copy.columns.closedAt,
                cell: (closure) => formatDateTime(locale, closure.closedAt),
              },
              {
                header: copy.columns.total,
                cell: (closure) => euro(closure.totalCents),
                numeric: true,
              },
              {
                header: copy.columns.documents,
                cell: (closure) => euro(closure.documentsCents),
                numeric: true,
              },
              {
                header: copy.columns.difference,
                cell: (closure) => euro(closure.differenceCents),
                numeric: true,
              },
              {
                header: copy.columns.now,
                cell: (closure) => (
                  <span
                    className={cn(
                      closure.supersededAt === null && closure.liveDifferenceCents !== 0
                        ? 'font-bold text-danger'
                        : '',
                    )}
                  >
                    {euro(closure.liveDifferenceCents)}
                  </span>
                ),
                numeric: true,
              },
              {
                header: copy.columns.reason,
                cell: (closure) => closure.differenceReason ?? '',
                className: 'break-words',
              },
              {
                header: copy.correct,
                headerHidden: true,
                cell: (closure) =>
                  closure.supersededAt === null && mayCorrect && open === null ? (
                    <Button
                      variant="link"
                      data-z={closure.id}
                      // Named by its Z and its point: two buttons of one name are none (UI-2g:
                      // the same Z number can be on two points' lists).
                      aria-label={`${format(copy.correctTitle, { number: closure.zNumber })}, ${point}`}
                      onClick={() => {
                        moved.current = { to: 'form', id: closure.id }
                        setCorrected(null)
                        setOpen(closure.id)
                      }}
                    >
                      {copy.correct}
                    </Button>
                  ) : null,
              },
            ]}
            after={(closure) => {
              const note =
                closure.supersededAt === null ? null : (
                  <p className="text-sm text-text-muted">
                    {format(copy.superseded, {
                      date: formatDateTime(locale, closure.supersededAt),
                      reason: closure.supersededReason ?? '',
                    })}
                  </p>
                )
              const form =
                open === closure.id ? (
                  <CorrectTill
                    wrong={closure}
                    point={point}
                    onDone={() => {
                      setOpen(null)
                      setCorrected(Date.now())
                    }}
                    onCancel={() => {
                      moved.current = { to: 'button', id: closure.id }
                      setOpen(null)
                    }}
                  />
                ) : null
              return note || form ? (
                <>
                  {note}
                  {form}
                </>
              ) : null
            }}
          />
          <p className="text-xs text-text-muted">{copy.liveHint}</p>
        </>
      )}
    </div>
  )
}
